import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import Footer from './shared/components/layout/Footer'
import LoadingScreen from './shared/components/loading/LoadingScreen'
import ErrorBoundary from './shared/error/ErrorBoundary'
import { AlertProvider } from './contexts/AlertContext'
import GlobalAlert from './shared/components/alerts/GlobalAlert'

import './App.css'
import './i18n'

/* ─────────────  Lazy-loaded pages  ───────────── */
const HomePage = lazy(() => import('./features/home/pages/HomePage'))
const ContactPage = lazy(() => import('./features/home/pages/ContactPage'))
const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'))
/*  const RegisterPage = lazy(() => import('./features/auth/pages/RegisterPage')) */
const TermsPage = lazy(() => import('./features/auth/pages/TermsPage'))
const PrivacyPolicyPage = lazy(
  () => import('./features/auth/pages/PrivacyPolicyPage'),
)
const CookiesPolicyPage = lazy(
  () => import('./features/auth/pages/CookiesPolicyPage'),
)
const DashboardPage = lazy(
  () => import('./features/dashboard/pages/DashboardPage'),
)
const WeatherPage = lazy(
  () => import('./features/weather/pages/WeatherPage'),
)
const MapPage = lazy(() => import('./features/maps/pages/MapPage'))
const ProfileDetailPage = lazy(
  () => import('./features/profile/pages/ProfileDetailPage'),
)
const ExploitationPage = lazy(
  () => import('./features/exploitations/pages/ExploitationPage'),
)
const ExploitationDetailPage = lazy(
  () => import('./features/exploitations/pages/ExploitationDetailPage'),
)
const ParcelPage = lazy(() => import('./features/parcels/pages/ParcelPage'))
const ParcelCreatePage = lazy(
  () => import('./features/parcels/pages/ParcelCreatePage'),
)
const ParcelDetailPage = lazy(
  () => import('./features/parcels/pages/ParcelDetailPage'),
)
const CultivationPage = lazy(
  () => import('./features/cultivations/pages/CultivationPage'),
)
const CultivationFormPage = lazy(
  () => import('./features/cultivations/pages/CultivationFormPage'),
)
const CultivationDetailPage = lazy(
  () => import('./features/cultivations/pages/CultivationDetailPage'),
)
const ToolPage = lazy(() => import('./features/tools/pages/ToolPage'))
const ToolDetailPage = lazy(
  () => import('./features/tools/pages/ToolDetailPage'),
)
const VehiclePage = lazy(() => import('./features/vehicles/pages/VehiclePage'))
const VehicleDetailPage = lazy(
  () => import('./features/vehicles/pages/VehicleDetailPage'),
)
const DevicePage = lazy(() => import('./features/devices/pages/DevicePage'))
const DeviceFormPage = lazy(
  () => import('./features/devices/pages/DeviceFormPage'),
)
const DeviceDetailPage = lazy(
  () => import('./features/devices/pages/DeviceDetailPage'),
)
const ActivityPage = lazy(
  () => import('./features/activities/pages/ActivityPage'),
)
const ActivityDetailPage = lazy(
  () => import('./features/activities/pages/ActivityDetailPage'),
)
const ActivityEditPage = lazy(
  () => import('./features/activities/pages/ActivityEditPage'),
)
const ActivityCreatePage = lazy(
  () => import('./features/activities/pages/ActivityCreatePage'),
)
const PrivateRoute = lazy(
  () => import('./features/auth/components/PrivateRoute'),
)
const AuthenticatedLayout = lazy(
  () => import('./shared/layout/AuthenticatedLayout'),
)

/* ─────────────  React-Query config  ───────────── */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min
      refetchOnWindowFocus: false,
      retry: 1,
      suspense: true,
    },
  },
})

/* ─────────────  Public layout  ───────────── */
const PublicLayout = () => (
  <div className="layout-container">
    <main className="main-content">
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </main>
    <Footer />
  </div>
)

/* ─────────────  Router  ───────────── */
const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'login', element: <LoginPage /> },
      /* { path: 'register', element: <RegisterPage /> }, */
      { path: 'terms', element: <TermsPage /> },
      { path: 'privacy', element: <PrivacyPolicyPage /> },
      { path: 'policy-privacy', element: <PrivacyPolicyPage /> },
      { path: 'policy-cookies', element: <CookiesPolicyPage /> },
    ],
  },
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <AuthenticatedLayout />
      </Suspense>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'map',
        element: (
          <PrivateRoute>
            <MapPage />
          </PrivateRoute>
        ),
      },
            {
        path: 'weather',
        element: (
          <PrivateRoute>
            <WeatherPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <PrivateRoute>
            <ProfileDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations',
        element: (
          <PrivateRoute>
            <ExploitationPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations/new',
        element: (
          <PrivateRoute>
            <ExploitationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'exploitations/:id',
        element: (
          <PrivateRoute>
            <ExploitationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels',
        element: (
          <PrivateRoute>
            <ParcelPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels/new',
        element: (
          <PrivateRoute>
            <ParcelCreatePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'parcels/:id',
        element: (
          <PrivateRoute>
            <ParcelDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations',
        element: (
          <PrivateRoute>
            <CultivationPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/new',
        element: (
          <PrivateRoute>
            <CultivationFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/:id',
        element: (
          <PrivateRoute>
            <CultivationDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'cultivations/:id/edit',
        element: (
          <PrivateRoute>
            <CultivationFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools',
        element: (
          <PrivateRoute>
            <ToolPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools/new',
        element: (
          <PrivateRoute>
            <ToolDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'tools/:id',
        element: (
          <PrivateRoute>
            <ToolDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <PrivateRoute>
            <ActivityPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/new',
        element: (
          <PrivateRoute>
            <ActivityCreatePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/:id',
        element: (
          <PrivateRoute>
            <ActivityDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'activities/:id/edit',
        element: (
          <PrivateRoute>
            <ActivityEditPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles',
        element: (
          <PrivateRoute>
            <VehiclePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles/new',
        element: (
          <PrivateRoute>
            <VehicleDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'vehicles/:id',
        element: (
          <PrivateRoute>
            <VehicleDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices',
        element: (
          <PrivateRoute>
            <DevicePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/new',
        element: (
          <PrivateRoute>
            <DeviceFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/:id',
        element: (
          <PrivateRoute>
            <DeviceDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'devices/:id/edit',
        element: (
          <PrivateRoute>
            <DeviceFormPage />
          </PrivateRoute>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])

/* ─────────────  App  ───────────── */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AlertProvider>
          <ErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <GlobalAlert />
              <RouterProvider router={router} />
            </Suspense>
          </ErrorBoundary>
        </AlertProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
